import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ICreateGateway,
  IGateway,
  IGatewayChirpstack,
  IGeoJSONPoint,
  IUpdateGateway,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { GatewayService } from '../../../../../auxiliares/servicios/http/gateways.service';

interface IGatewayMasivo {
  gatewayEui: string;
  nombre: string;
}

interface IValidationError {
  line: number;
  error: string;
}

@Component({
  selector: 'app-crear-editar-gateway',
  templateUrl: './crear-editar-gateway.component.html',
  styleUrl: './crear-editar-gateway.component.scss',
  standalone: false,
})
export class CrearEditarGatewayComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  private service = inject(GatewayService);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private mapaService = inject(MapaFacilService);
  data = inject<{
    edit?: IGateway;
  }>(MAT_DIALOG_DATA, { optional: true });
  dialogRef? = inject<MatDialogRef<CrearEditarGatewayComponent>>(MatDialogRef, {
    optional: true,
  });

  public clientePropio = LoginService.getCliente();
  public id?: string | null;
  public title?: string;
  public subtitle?: string;
  public gateway?: IGateway;
  public gatewayNetwork: IGatewayChirpstack;
  public loading = true;

  public isEditing?: boolean;
  public isSkipFcntCheckReadOnly = false;
  public isDialog: boolean = false;

  public form?: FormGroup;
  public formMasivo?: FormGroup;

  public tipoCliente = HelperService.getTipoCliente();

  // Propiedades para carga masiva
  public modoMasivo = false;
  public csvData = '';
  public validationErrors: IValidationError[] = [];
  public processingMasivo = false;

  // Lista de gateways disponibles desde Chirpstack
  public gatewaysChirpstack: IGatewayChirpstack[] = [];
  public gatewaySeleccionado?: IGatewayChirpstack;

  // Mapa / ubicación
  public ubicacion?: IGeoJSONPoint;
  private marker$?: Subscription;

  constructor() {
    this.isDialog = !!this.dialogRef;
  }

  get mostrarCamposCliente(): boolean {
    return this.tipoCliente !== 'Final';
  }

  // Alternar entre modo individual y masivo
  public toggleModoMasivo(): void {
    this.modoMasivo = !this.modoMasivo;
    this.csvData = '';
    this.validationErrors = [];

    if (this.modoMasivo) {
      this.createFormMasivo();
    } else {
      this.createForm();
    }
  }

  // Crear formulario para carga masiva
  private createFormMasivo(): void {
    const clienteDefault =
      this.tipoCliente === 'Final' ? this.clientePropio._id : null;

    this.formMasivo = new FormGroup({
      idCliente: new FormControl(clienteDefault, Validators.required),
    });
  }

  private createForm() {
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : this.gateway?.idCliente;

    this.form = new FormGroup({
      nombre: new FormControl(this.gateway?.nombre, [Validators.required]),
      gatewayEui: new FormControl(this.gateway?.gatewayEui, [
        Validators.required,
      ]),
      idCliente: new FormControl(clienteDefault, [Validators.required]),
    });
  }

  // Método para cargar gateways desde Chirpstack
  private async cargarGatewaysChirpstack(): Promise<void> {
    try {
      this.gatewaysChirpstack = await this.service.getChirpstack();
    } catch (error) {
      console.error('Error cargando gateways de Chirpstack:', error);
      this.helper.notifError('Error al cargar gateways de Chirpstack');
    }
  }

  // Método llamado cuando se selecciona un gateway del selector
  public async onGatewaySeleccionado(gatewayEui: string): Promise<void> {
    // Buscar en la lista básica
    const gwBasico = this.gatewaysChirpstack.find(
      (gw) => gw.gatewayId === gatewayEui,
    );

    if (gwBasico) {
      try {
        // Consultar datos completos del gateway desde Chirpstack usando su _id
        // Nota: consultarChirpstack espera el ID de la BD, no el gatewayEui
        // Por ahora usamos los datos básicos disponibles
        this.gatewaySeleccionado = gwBasico;

        // Actualizar el formulario con el gatewayEui seleccionado
        this.form.patchValue({
          gatewayEui: gwBasico.gatewayId,
        });
      } catch (error) {
        console.error('Error al consultar gateway en Chirpstack:', error);
        this.helper.notifError('Error al obtener datos del gateway');
      }
    }
  }

  // Validar CSV para carga masiva
  private validateCSV(): IGatewayMasivo[] | null {
    this.validationErrors = [];
    const lines = this.csvData.trim().split('\n');
    const gateways: IGatewayMasivo[] = [];
    const gatewayEuisUsados = new Set<string>();
    const nombresUsados = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const params = line.split(',').map((p) => p.trim());
      const lineNumber = i + 1;

      // Validar cantidad de parámetros
      if (params.length !== 2) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'Debe tener exactamente 2 parámetros (nombre, gatewayEui)',
        });
        continue;
      }

      const [nombre, gatewayEui] = params;

      // Validar deveui
      if (!gatewayEui || !/^[0-9a-fA-F]{16}$/.test(gatewayEui)) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'gatewayEUI debe ser hexadecimal de 16 caracteres',
        });
        continue;
      }

      if (gatewayEuisUsados.has(gatewayEui.toUpperCase())) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'gatewayEUI duplicado',
        });
        continue;
      }

      // Validar nombre
      if (!nombre) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'Nombre es obligatorio',
        });
        continue;
      }

      if (nombresUsados.has(nombre)) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'Nombre duplicado',
        });
        continue;
      }

      gatewayEuisUsados.add(gatewayEui.toUpperCase());
      nombresUsados.add(nombre);

      gateways.push({
        gatewayEui,
        nombre,
      });
    }

    return this.validationErrors.length === 0 ? gateways : null;
  }

  // Procesar carga masiva
  public async procesarCargaMasiva(): Promise<void> {
    const gateways = this.validateCSV();

    if (!gateways) {
      this.helper.notifError('Corrija los errores antes de continuar');
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Crear Dispositivos Masivamente',
      `¿Querés crear ${gateways.length} gateway(s)?`,
      'Aceptar',
      'Cancelar',
    );

    if (!confirmed) return;

    this.processingMasivo = true;
    let creados = 0;
    const lineasProcesadas: number[] = [];

    for (let i = 0; i < gateways.length; i++) {
      const gateway = gateways[i];

      try {
        // Buscar el gateway en la lista de Chirpstack
        const gwChirpstack = this.gatewaysChirpstack.find(
          (gw) => gw.gatewayId === gateway.gatewayEui,
        );

        if (!gwChirpstack) {
          console.error(
            `Gateway ${gateway.gatewayEui} no encontrado en Chirpstack`,
          );
          continue;
        }

        const create: ICreateGateway = {
          nombre: gateway.nombre,
          gatewayEui: gateway.gatewayEui,
          idCliente: this.formMasivo.value.idCliente,
          description: gwChirpstack.description,
        };

        await this.service.create(create);
        creados++;
        lineasProcesadas.push(i);
      } catch (error) {
        console.error(`Error creando gateway ${gateway.nombre}:`, error);
      }
    }

    // Eliminar líneas procesadas exitosamente del textarea
    if (lineasProcesadas.length > 0) {
      const lines = this.csvData.trim().split('\n');
      const remainingLines = lines.filter(
        (_, index) => !lineasProcesadas.includes(index),
      );
      this.csvData = remainingLines.join('\n');
    }

    this.processingMasivo = false;

    if (creados === gateways.length) {
      this.helper.notifSuccess(
        `Se crearon ${creados} de ${gateways.length} gateways`,
      );
      if (creados === gateways.length) {
        if (this.dialogRef) {
          //Si es un diálogo, cerramos sólo es el dialog
          this.dialogRef.close();
        } else {
          this.helper.volver(); //Si no es un dialog, volvemos a la página anterior
        }
      }
    } else if (creados > 0) {
      this.helper.notifSuccess(
        `Se crearon ${creados} de ${gateways.length} dispositivos`,
      );
    } else {
      this.helper.notifError('No se crearon dispositivos');
    }
  }

  // MAPA

  public iniciarMapa(): void {
    const coords = this.ubicacion?.coordinates;
    const tieneUbicacion =
      coords?.length === 2 && !(coords[0] === 0 && coords[1] === 0);

    // Centrar el mapa: en la ubicación del gateway si existe, o en Buenos Aires
    const map = this.mapaService.getMap();
    if (map) {
      if (tieneUbicacion) {
        map.flyTo({
          center: coords as [number, number],
          zoom: 15,
          duration: 800,
        });
      } else {
        map.flyTo({ center: [-58.3816, -34.6037], zoom: 12, duration: 800 });
      }
    }

    // Suscribirse a cambios de marcador para actualizar ubicación
    this.marker$ = this.mapaService.marker.subscribe((coord) => {
      if (coord) {
        this.ubicacion = {
          type: 'Point',
          coordinates: coord as [number, number],
        };
      }
    });

    // Colocar marcador si el gateway ya tiene ubicación
    if (tieneUbicacion) {
      this.mapaService.addMarker(coords as [number, number]);
    }
  }

  public get tieneUbicacionGatewaySeleccionado(): boolean {
    const long = this.gatewaySeleccionado?.location?.longitude;
    const lat = this.gatewaySeleccionado?.location?.latitude;

    return typeof long === 'number' && typeof lat === 'number';
  }

  public usarUbicacionGatewaySeleccionado(): void {
    const long = this.gatewaySeleccionado?.location?.longitude;
    const lat = this.gatewaySeleccionado?.location?.latitude;

    if (typeof long !== 'number' || typeof lat !== 'number') {
      this.helper.notifError(
        'El gateway seleccionado no tiene ubicación disponible',
      );
      return;
    }

    const coord: [number, number] = [long, lat];

    this.ubicacion = {
      type: 'Point',
      coordinates: coord,
    };

    this.mapaService.addMarker(coord);

    const map = this.mapaService.getMap();
    if (map) {
      map.flyTo({
        center: coord,
        zoom: 15,
        duration: 800,
      });
    }

    this.helper.notifSuccess('Ubicación cargada desde Chirpstack');
  }

  ngOnDestroy(): void {
    this.marker$?.unsubscribe();
  }

  private async listar(): Promise<void> {
    this.gateway = await firstValueFrom(this.lista2.gateway(this.id!));
  }

  public async onSubmit() {
    if (this.modoMasivo) {
      await this.procesarCargaMasiva();
      return;
    }

    let res: any;

    if (this.id) {
      const confirmed = await this.dialogService.confirm(
        `${this.title}`,
        `¿Querés ${this.title.toLowerCase()}?`,
        'Aceptar',
        'Cancelar',
      );
      if (!confirmed) return;

      try {
        const update = this.getData() as IUpdateGateway;
        await this.service.update(this.id, update);
        this.helper.notifSuccess('Gateway Editado');
        if (this.dialogRef) {
          //Si es un diálogo, cerramos sólo es el dialog
          this.dialogRef.close(update);
        } else {
          this.helper.volver(); //Si no es un dialog, volvemos a la página anterior
        }
      } catch (error) {
        console.error(error);
        this.helper.notifError(error);
      }
    } else {
      const confirmed = await this.dialogService.confirm(
        `${this.title}`,
        `¿Querés ${this.title.toLowerCase()}?`,
        'Aceptar',
        'Cancelar',
      );
      if (!confirmed) return;

      try {
        const create = this.getData() as ICreateGateway;
        await this.service.create(create);
        this.helper.notifSuccess('Gateway Creado');
        if (this.dialogRef) {
          //Si es un diálogo, cerramos sólo es el dialog
          this.dialogRef.close(create);
        } else {
          this.helper.volver(); //Si no es un dialog, volvemos a la página anterior
        }
      } catch (error) {
        console.error(error);
        this.helper.notifError(error);
      }
    }
  }

  public volver(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    this.helper.volver();
  }

  private getData() {
    const formData = this.form?.value;

    const data: ICreateGateway | IUpdateGateway = {
      nombre: formData.nombre,
      gatewayEui: formData.gatewayEui,
      idCliente: formData.idCliente,
    };

    // Si estamos editando, incluir ubicación del mapa si fue definida
    if (this.id && this.ubicacion) {
      data.ubicacion = this.ubicacion;
    }

    // Si estamos creando (no editando), agregar datos del gateway seleccionado
    if (!this.id && this.gatewaySeleccionado) {
      data.description = this.gatewaySeleccionado.description;
      data.fechaCreacionChirpstack = this.gatewaySeleccionado.createdAt;
      const long = this.gatewaySeleccionado.location?.longitude;
      const lat = this.gatewaySeleccionado.location?.latitude;
      const ubicacion: IGeoJSONPoint = {
        type: 'Point',
        coordinates: [long, lat],
      };
      data.ubicacion = ubicacion;
      data.nombreChirpstack = this.gatewaySeleccionado.name;
    }

    return data;
  }

  private setTitles() {
    // Por modo (no por ubicación): un gateway sin ubicación también se edita
    if (this.isEditing) {
      this.title = 'Editar Gateway';
      this.subtitle = this.gateway?.nombre;
    } else {
      this.title = 'Crear Gateway';
    }
  }

  async ngOnInit() {
    this.loading = true;
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id') || this.data?.edit?._id;

    // Cargar gateways desde Chirpstack
    await this.cargarGatewaysChirpstack();

    if (this.id) {
      this.isEditing = true;
      await this.listar();
      // Si estamos editando, buscar el gateway seleccionado
      this.gatewaySeleccionado = this.gatewaysChirpstack.find(
        (gw) =>
          gw.gatewayId.toLowerCase() === this.gateway?.gatewayEui.toLowerCase(),
      );
      // Cargar ubicación existente del gateway
      const loc = this.gateway?.ubicacion;
      this.ubicacion = loc;
    } else {
      this.isEditing = false;
    }

    this.setTitles();
    this.createForm();
    if (this.tipoCliente === 'Final') {
      this.form.patchValue({
        idCliente: this.clientePropio._id,
      });
    }
    this.loading = false;
  }
}
